/**
 * 题目数据验证器
 */

import type { ValidationResult, ValidationError, FieldDefinition } from './types';
import type { SectionQuestionTypeConstraint } from './types';
import { getQuestionTypeMetadata, getExamQuestionTypeDefinition, getQuestionTypeForExam } from './registry';
import { normalizeStimulus, questionHasStimulus } from './stimulus';
import { PAPER_ORCHESTRATION_FIELD_IDS } from './paperOrchestration';
import { resolveExamLevelFieldValue } from './examLevelFields';

/** 验证选项 */
export interface ValidateOptions {
  /** 'strict' = 完整验证（默认）；'preview' = AI 预览模式，跳过 audioUrl 等后置字段检查 */
  mode?: 'strict' | 'preview';
  /**
   * Optional Skill whitelist of base/type ids allowed for this Platform Scope.
   * When provided, question.type must match one of these (or their base types).
   */
  allowedQuestionTypes?: string[];
  /** 有则走考试特化路径（registry 已由 exams 注册） */
  examId?: string;
  /** e.g. toefl_writing — 与 examId 成对传入 */
  typeId?: string;
}

const WARNING_ERROR_CODES = new Set([
  'CONTENT_FORMAT_WARNING',
  'CONTENT_SEMANTIC_WARNING',
  'MISSING_EXPLANATION',
  'EXPLANATION_RESTATES_ANSWER',
  'OPTION_CONTENT_TOO_SHORT',
  'SKILL_TYPE_MISMATCH',
]);

/** 仅当题干含可大小写的拉丁字母且整段为大写时视为格式风险（Hangul/CJK 无大小写）。 */
function looksLikeAllCapsLatin(content: string): boolean {
  if (!/[A-Za-z\u00C0-\u024F]/.test(content)) return false;
  return content === content.toUpperCase();
}

export function isBlockingValidationError(error: ValidationError): boolean {
  if (error.severity === 'warning') return false;
  if (error.severity === 'blocking') return true;
  if (error.code && WARNING_ERROR_CODES.has(error.code)) return false;
  return true;
}

function finalizeValidationResult(errors: ValidationError[]): ValidationResult {
  const normalized = errors.map((error) => ({
    ...error,
    severity:
      error.severity ??
      (error.code && WARNING_ERROR_CODES.has(error.code) ? ('warning' as const) : ('blocking' as const)),
  }));
  const warnings = normalized.filter((error) => error.severity === 'warning');
  const blocking = normalized.filter((error) => error.severity === 'blocking');
  return {
    valid: blocking.length === 0,
    errors: normalized,
    warnings,
  };
}

/**
 * 基础类型校验（不含考试特化定义）。
 * 考试路径由 validateQuestionWithExamDefinition 组合调用，避免与 validateQuestion 递归。
 */
function validateQuestionBase(question: any, options?: ValidateOptions): ValidationResult {
  if (!question || typeof question !== 'object') {
    return finalizeValidationResult([
      {
        field: 'root',
        message: '题目数据必须是对象',
        code: 'INVALID_TYPE',
        severity: 'blocking',
      },
    ]);
  }

  if (!question.type) {
    return finalizeValidationResult([
      {
        field: 'type',
        message: '题目类型不能为空',
        code: 'MISSING_TYPE',
        severity: 'blocking',
      },
    ]);
  }

  const metadata = getQuestionTypeMetadata(question.type);
  if (!metadata) {
    return finalizeValidationResult([
      {
        field: 'type',
        message: `未知的题目类型: ${question.type}`,
        code: 'UNKNOWN_TYPE',
        severity: 'blocking',
      },
    ]);
  }

  const typeValidation = metadata.validate(question);

  const isPreview = options?.mode === 'preview';
  const typeErrors = isPreview
    ? typeValidation.errors.filter((e: ValidationError) => e.code !== 'MISSING_AUDIO')
    : typeValidation.errors;

  const qualityErrors = validateQuestionQuality(question, options);
  const customErrors = applyCustomValidationRules(question);

  return finalizeValidationResult([...typeErrors, ...qualityErrors, ...customErrors]);
}

/**
 * 验证题目数据。
 * 传入 examId + typeId 时走考试特化定义（extraFields / customValidator / open_ended）。
 */
export function validateQuestion(question: any, options?: ValidateOptions): ValidationResult {
  if (options?.examId && options?.typeId) {
    return validateQuestionWithExamDefinition(question, options.examId, options.typeId, options);
  }
  return validateQuestionBase(question, options);
}

function isAbsentOpenEndedAnswer(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim().length === 0) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/** 开放作答题干不应出现完形空格（writing/speaking 挂 fill_blank 载体时常见污染） */
export function looksLikeOpenEndedClozeStem(content: unknown): boolean {
  const text = String(content ?? '');
  if (!text.trim()) return false;
  return (
    /_{3,}/.test(text) ||
    /\[填空\d*\]/.test(text) ||
    /\[\s*blank\s*\d*\s*\]/i.test(text) ||
    /\(\s*_{2,}\s*\)/.test(text)
  );
}

/**
 * 验证题目质量
 * 添加更严格的验证规则和语义分析
 */
function validateQuestionQuality(question: any, options?: ValidateOptions): ValidationError[] {
  const errors: ValidationError[] = [];
  const isPreview = options?.mode === 'preview';

  // 题干缺失（含 null）
  const rawContent = question.content;
  if (rawContent == null || String(rawContent).trim().length === 0) {
    errors.push({
      field: 'content',
      message: '题目题干不能为空',
      code: 'MISSING_CONTENT',
      severity: 'blocking',
    });
  }

  // 验证题目内容长度
  if (question.content) {
    const content = String(question.content).trim();

    if (content.length < 5) {
      errors.push({
        field: 'content',
        message: '题目内容过短，至少需要5个字符',
        code: 'CONTENT_TOO_SHORT',
        severity: 'blocking',
      });
    }

    if (content.length > 5000) {
      errors.push({
        field: 'content',
        message: '题目内容过长，不能超过5000个字符',
        code: 'CONTENT_TOO_LONG',
        severity: 'blocking',
      });
    }

    // 全大写警告仅针对有大小写的字母脚本；Hangul / 汉字 / 假名无大小写，toUpperCase 恒等会误报
    if (looksLikeAllCapsLatin(content) && content.length > 20) {
      errors.push({
        field: 'content',
        message: '题目内容可能全为大写，请检查格式',
        code: 'CONTENT_FORMAT_WARNING',
        severity: 'warning',
      });
    }

    // 语义分析：检查题目是否包含疑问词或问题标记
    const questionMarkers = [
      // 中文疑问词
      '？',
      '?',
      '什么',
      '哪个',
      '哪些',
      '如何',
      '为什么',
      '是否',
      '能否',
      // 英文疑问词
      'what',
      'which',
      'who',
      'whom',
      'whose',
      'where',
      'when',
      'why',
      'how',
      // 法语疑问词
      'quel',
      'quelle',
      'quels',
      'quelles',
      'qui',
      'que',
      'quoi',
      'où',
      'comment',
      'pourquoi',
      'combien',
      // 其他常见疑问词
      "qu'est-ce que",
      "qu'est-ce qui",
      'est-ce que',
      'est-ce qui',
      // 韩语疑问 / TOPIK 指令尾
      '무엇입니까',
      '어디',
      '언제',
      '왜',
      '어떻게',
      '어느',
      '누구',
      '습니까',
      '인가요',
      '까요',
      '세요',
      '십시오',
      '고르십시오',
      // 日语疑问 / JLPT 指令尾
      'ですか',
      'ますか',
      'でしょうか',
      'なさい',
      '選びなさい',
      'どれ',
      'どこ',
      'いつ',
      'なぜ',
      'どうして',
      '誰',
      // 西语疑问
      '¿',
      'qué',
      'cuál',
      'quién',
      'dónde',
      'cuándo',
      'cómo',
      'por qué',
      'cuánto',
    ];
    const hasQuestionMarker = questionMarkers.some((marker) =>
      content.toLowerCase().includes(marker.toLowerCase())
    );

    // 对于选择题，如果包含空白（多个下划线），允许没有疑问词
    // 这是常见的题目格式，如："À la suite d'un mouvement social, un bus sur quatre ______."
    const hasBlank = /_{3,}/.test(content); // 3个或更多下划线
    const isChoiceQuestion = ['single_choice', 'multiple_choice'].includes(question.type);
    const hasOptions = question.options && question.options.length > 0;
    const isBlankFormatQuestion = isChoiceQuestion && hasBlank && hasOptions;

    if (!hasQuestionMarker && content.length > 10 && !isBlankFormatQuestion) {
      // 警告：题目可能缺少疑问词
      errors.push({
        field: 'content',
        message: '题目可能缺少疑问词或问题标记，请检查是否为完整的问题',
        code: 'CONTENT_SEMANTIC_WARNING',
        severity: 'warning',
      });
    }

    // 检查是否包含明显的占位符或未完成内容
    // 对于选择题，如果包含多个下划线（3个或更多）且有选项，视为正常格式（题目中的空白）
    // 例如："À la suite d'un mouvement social, un bus sur quatre ______."
    const hasMultipleUnderscores = /_{3,}/.test(content); // 3个或更多连续下划线
    const isNormalBlank = isChoiceQuestion && hasMultipleUnderscores && hasOptions;

    // 只检查明显的占位符（不包含下划线的情况）
    const placeholders = ['...', '待填写', 'TODO', 'FIXME', '待补充', '待完善'];
    const hasPlaceholder = placeholders.some((ph) => content.includes(ph));

    // 对于1-2个下划线，如果不是选择题的正常空白格式，才视为占位符
    // 检查是否有独立的1-2个下划线（前后不是下划线）
    let hasSingleOrDoubleUnderscore = false;
    if (!isNormalBlank) {
      // 检查是否有1-2个下划线，且前后不是下划线
      const underscorePattern = /[^_]_[^_]|[^_]_$|^_[^_]|[^_]__[^_]|[^_]__$|^__[^_]/;
      hasSingleOrDoubleUnderscore = underscorePattern.test(content);
    }

    // 如果包含明显的占位符，或者有1-2个下划线但不是正常格式，则报错
    if (hasPlaceholder || hasSingleOrDoubleUnderscore) {
      errors.push({
        field: 'content',
        message: '题目内容包含占位符，可能未完成',
        code: 'CONTENT_PLACEHOLDER',
        severity: 'blocking',
      });
    }

    // 检查重复字符（可能是格式错误）
    if (/(.)\1{10,}/.test(content)) {
      errors.push({
        field: 'content',
        message: '题目内容包含大量重复字符，可能格式有误',
        code: 'CONTENT_REPEAT_CHARS',
        severity: 'blocking',
      });
    }
  }

  const stimulus = normalizeStimulus(question.stimulus);

  // stimulus 含 passage → 必须有阅读材料
  if (questionHasStimulus(question, 'passage')) {
    const passage = question.passage;
    if (passage == null || String(passage).trim().length === 0) {
      errors.push({
        field: 'passage',
        message: '题目声明了 passage 材料但缺少阅读材料',
        code: 'MISSING_PASSAGE',
        severity: 'blocking',
      });
    }
  }

  // stimulus 含 audio → strict 下必须有音频（preview 交给 TTS）
  if (questionHasStimulus(question, 'audio') && !isPreview) {
    if (!question.audioUrl && question.audioStatus !== 'pending') {
      errors.push({
        field: 'audioUrl',
        message: '题目声明了 audio 材料但缺少音频',
        code: 'MISSING_AUDIO',
        severity: 'blocking',
      });
    }
  }

  // stimulus 含 image → 应有 imageUrl（warning，避免误伤可选配图流程）
  if (questionHasStimulus(question, 'image')) {
    if (!question.imageUrl || String(question.imageUrl).trim().length === 0) {
      errors.push({
        field: 'imageUrl',
        message: '题目声明了 image 材料但缺少图片 URL',
        code: 'MISSING_IMAGE',
        severity: 'warning',
      });
    }
  }

  // 非法 stimulus 值已由 normalize 丢弃；若原始输入含未知项则 warning
  if (Array.isArray(question.stimulus)) {
    const raw = question.stimulus.filter((v: unknown) => typeof v === 'string') as string[];
    const unknown = raw.filter((v) => !stimulus.includes(v.trim().toLowerCase() as (typeof stimulus)[number]));
    if (unknown.length > 0) {
      errors.push({
        field: 'stimulus',
        message: `未知的材料类型: ${unknown.join(', ')}`,
        code: 'UNKNOWN_STIMULUS',
        severity: 'warning',
      });
    }
  }

  // 解析缺失或仅复述答案
  const explanation =
    question.explanation == null ? '' : String(question.explanation).trim();
  if (!explanation) {
    errors.push({
      field: 'explanation',
      message: '缺少解析，教学价值不足',
      code: 'MISSING_EXPLANATION',
      severity: 'warning',
    });
  } else {
    const answerText = Array.isArray(question.correctAnswer)
      ? question.correctAnswer.map(String).join(' ')
      : String(question.correctAnswer ?? '');
    if (
      answerText.trim() &&
      explanation.replace(/\s+/g, '').toLowerCase() ===
        answerText.replace(/\s+/g, '').toLowerCase()
    ) {
      errors.push({
        field: 'explanation',
        message: '解析仅复述答案，缺少教学说明',
        code: 'EXPLANATION_RESTATES_ANSWER',
        severity: 'warning',
      });
    }
  }

  // Skill 题型白名单（由调用方传入 allowedQuestionTypes）
  if (options?.allowedQuestionTypes?.length) {
    const allowed = new Set(options.allowedQuestionTypes.map((id) => id.trim().toLowerCase()));
    const typeId = String(question.type ?? '')
      .trim()
      .toLowerCase();
    if (typeId && !allowed.has(typeId)) {
      errors.push({
        field: 'type',
        message: `题型 ${question.type} 不在当前 Skill 白名单内`,
        code: 'SKILL_TYPE_MISMATCH',
        severity: 'warning',
      });
    }
  }

  // 验证选项（对于选择题）
  if (question.options && Array.isArray(question.options)) {
    if (question.options.length < 2) {
      errors.push({
        field: 'options',
        message: '选择题至少需要2个选项',
        code: 'OPTIONS_TOO_FEW',
        severity: 'blocking',
      });
    }

    if (question.options.length > 10) {
      errors.push({
        field: 'options',
        message: '选择题选项过多，不能超过10个',
        code: 'OPTIONS_TOO_MANY',
        severity: 'blocking',
      });
    }

    // 验证每个选项的内容
    question.options.forEach((opt: any, index: number) => {
      if (!opt || typeof opt !== 'object') {
        errors.push({
          field: `options[${index}]`,
          message: `选项 ${index + 1} 格式不正确`,
          code: 'OPTION_INVALID',
          severity: 'blocking',
        });
        return;
      }

      if (!opt.content || String(opt.content).trim().length === 0) {
        errors.push({
          field: `options[${index}].content`,
          message: `选项 ${index + 1} 内容不能为空`,
          code: 'OPTION_CONTENT_EMPTY',
          severity: 'blocking',
        });
      }

      if (opt.content && String(opt.content).trim().length > 1000) {
        errors.push({
          field: `options[${index}].content`,
          message: `选项 ${index + 1} 内容过长，不能超过1000个字符`,
          code: 'OPTION_CONTENT_TOO_LONG',
          severity: 'blocking',
        });
      }

      // 检查选项内容是否过短（可能是未完成）
      if (opt.content && String(opt.content).trim().length < 2) {
        errors.push({
          field: `options[${index}].content`,
          message: `选项 ${index + 1} 内容过短，可能未完成`,
          code: 'OPTION_CONTENT_TOO_SHORT',
          severity: 'warning',
        });
      }

      // 检查选项是否重复
      const otherOptions = question.options.filter((_o: any, i: number) => i !== index);
      const duplicateOption = otherOptions.find(
        (o: any) => o.content && String(o.content).trim() === String(opt.content).trim()
      );
      if (duplicateOption) {
        errors.push({
          field: `options[${index}].content`,
          message: `选项 ${index + 1} 与其他选项内容重复`,
          code: 'OPTION_DUPLICATE',
          severity: 'blocking',
        });
      }
    });

    // 验证选项ID唯一性
    const optionIds = question.options.map((opt: any) => opt.id).filter(Boolean);
    const uniqueIds = new Set(optionIds);
    if (optionIds.length !== uniqueIds.size) {
      errors.push({
        field: 'options',
        message: '选项ID必须唯一',
        code: 'OPTION_IDS_DUPLICATE',
        severity: 'blocking',
      });
    }
  }

  // 验证正确答案
  if (question.correctAnswer !== undefined && question.correctAnswer !== null) {
    // 对于选择题，验证答案是否在选项中
    if (question.options && Array.isArray(question.options)) {
      const optionIds = question.options.map((opt: any) => opt.id).filter(Boolean);

      if (Array.isArray(question.correctAnswer)) {
        // 多选题
        const invalidAnswers = question.correctAnswer.filter(
          (ans: any) => !optionIds.includes(String(ans))
        );
        if (invalidAnswers.length > 0) {
          errors.push({
            field: 'correctAnswer',
            message: `答案 ${invalidAnswers.join(', ')} 不在选项中`,
            code: 'ANSWER_NOT_IN_OPTIONS',
            severity: 'blocking',
          });
        }

        if (question.correctAnswer.length === 0) {
          errors.push({
            field: 'correctAnswer',
            message: '多选题至少需要一个正确答案',
            code: 'NO_CORRECT_ANSWER',
            severity: 'blocking',
          });
        }
      } else {
        // 单选题
        const answerStr = String(question.correctAnswer);
        if (!optionIds.includes(answerStr)) {
          errors.push({
            field: 'correctAnswer',
            message: `答案 ${answerStr} 不在选项中`,
            code: 'ANSWER_NOT_IN_OPTIONS',
            severity: 'blocking',
          });
        }
      }
    }
  } else if (question.options && Array.isArray(question.options) && question.options.length > 0) {
    errors.push({
      field: 'correctAnswer',
      message: '缺少正确答案',
      code: 'MISSING_CORRECT_ANSWER',
      severity: 'blocking',
    });
  }

  // 验证解析内容长度
  if (question.explanation && String(question.explanation).length > 5000) {
    errors.push({
      field: 'explanation',
      message: '解析内容过长，不能超过5000个字符',
      code: 'EXPLANATION_TOO_LONG',
      severity: 'blocking',
    });
  }

  return errors;
}

/**
 * 验证字段
 */
export function validateField(
  fieldName: string,
  value: any,
  definition: FieldDefinition
): ValidationError | null {
  // 检查必需字段
  if (definition.required && (value === undefined || value === null || value === '')) {
    return {
      field: fieldName,
      message: `${fieldName} 是必需字段`,
      code: 'REQUIRED_FIELD_MISSING',
    };
  }

  // 如果字段为空且不是必需的，跳过验证
  if (!definition.required && (value === undefined || value === null || value === '')) {
    return null;
  }

  // 类型验证
  const typeError = validateFieldType(fieldName, value, definition.type);
  if (typeError) {
    return typeError;
  }

  // 自定义验证器
  if (definition.validator) {
    const result = definition.validator(value);
    if (result !== true) {
      return {
        field: fieldName,
        message: typeof result === 'string' ? result : `${fieldName} 验证失败`,
        code: 'VALIDATION_FAILED',
      };
    }
  }

  return null;
}

/**
 * 验证字段类型
 */
function validateFieldType(
  fieldName: string,
  value: any,
  expectedType: FieldDefinition['type']
): ValidationError | null {
  switch (expectedType) {
    case 'string':
      if (typeof value !== 'string') {
        return {
          field: fieldName,
          message: `${fieldName} 必须是字符串`,
          code: 'TYPE_MISMATCH',
        };
      }
      break;

    case 'number':
      if (typeof value !== 'number' || isNaN(value)) {
        return {
          field: fieldName,
          message: `${fieldName} 必须是数字`,
          code: 'TYPE_MISMATCH',
        };
      }
      break;

    case 'boolean':
      if (typeof value !== 'boolean') {
        return {
          field: fieldName,
          message: `${fieldName} 必须是布尔值`,
          code: 'TYPE_MISMATCH',
        };
      }
      break;

    case 'array':
      if (!Array.isArray(value)) {
        return {
          field: fieldName,
          message: `${fieldName} 必须是数组`,
          code: 'TYPE_MISMATCH',
        };
      }
      break;

    case 'object':
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return {
          field: fieldName,
          message: `${fieldName} 必须是对象`,
          code: 'TYPE_MISMATCH',
        };
      }
      break;

    case 'url':
      if (typeof value !== 'string' || !isValidUrl(value)) {
        return {
          field: fieldName,
          message: `${fieldName} 必须是有效的 URL`,
          code: 'INVALID_URL',
        };
      }
      break;

    case 'text':
      if (typeof value !== 'string') {
        return {
          field: fieldName,
          message: `${fieldName} 必须是文本`,
          code: 'TYPE_MISMATCH',
        };
      }
      break;
  }

  return null;
}

/**
 * 验证 URL
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    // 也支持相对路径
    return url.startsWith('/') || url.startsWith('./');
  }
}

/**
 * 获取验证错误信息（用于显示）
 */
export function getValidationErrors(result: ValidationResult): string[] {
  return result.errors.map((err) => `${err.field}: ${err.message}`);
}

/**
 * 验证题目数组
 * 增强：添加相似度检测
 */
export function validateQuestions(
  questions: any[],
  options?: ValidateOptions
): {
  valid: boolean;
  results: Array<{ index: number; result: ValidationResult }>;
  duplicateWarnings?: Array<{ index1: number; index2: number; similarity: number }>;
} {
  const results = questions.map((question, index) => ({
    index,
    result: validateQuestion(question, options),
  }));

  const valid = results.every((r) => r.result.valid);

  // 检测相似题目
  const duplicateWarnings = detectSimilarQuestions(questions);

  return {
    valid,
    results,
    duplicateWarnings,
  };
}

export type SimilarQuestionPair = {
  index1: number;
  index2: number;
  similarity: number;
};

export type DetectSimilarQuestionsOptions = {
  /** Jaccard 阈值，默认 0.8（与批内校验一致） */
  similarityThreshold?: number;
  /** 最多参与两两比较的题量（从头部截断）；默认不截断 */
  maxQuestions?: number;
  /** 最多返回的相似对数量；默认不截断 */
  maxPairs?: number;
};

export type SimilarQuestionInput = {
  content?: unknown;
  /** 听力/阅读材料；若被误塞进 content，比对前会剥掉（材料复用本身不算内容重复） */
  passage?: unknown;
  /** 选择题选项；进指纹（忽略 id / isCorrect，按文本排序） */
  options?: Array<{ content?: unknown } | string> | null;
  /** open_ended 或无有效选项 → 指纹仅题干 */
  responseMode?: 'closed' | 'open_ended' | string | null;
};

/**
 * 内容近重复指纹：规范化题干 +（选择题）排序后的选项文本。
 * 不进指纹：topics / passage / explanation / tags。
 */
export function buildSimilarityFingerprint(question: SimilarQuestionInput): string {
  const stem = normalizeSimilarityText(question.content, question.passage);
  if (!stem) return '';

  const openEnded =
    question.responseMode === 'open_ended' || !hasComparableOptions(question.options);
  if (openEnded) return stem;

  const optionTexts = collectOptionTexts(question.options);
  if (optionTexts.length === 0) return stem;

  return `${stem}\n||\n${optionTexts.join('\n')}`;
}

function hasComparableOptions(
  options: SimilarQuestionInput['options']
): boolean {
  return collectOptionTexts(options).length >= 2;
}

function collectOptionTexts(options: SimilarQuestionInput['options']): string[] {
  if (!Array.isArray(options) || options.length === 0) return [];
  const texts: string[] = [];
  for (const opt of options) {
    const raw =
      typeof opt === 'string'
        ? opt
        : opt && typeof opt === 'object'
          ? String(opt.content ?? '')
          : '';
    const normalized = raw.replace(/\s+/g, ' ').trim().toLowerCase();
    if (normalized) texts.push(normalized);
  }
  texts.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  return texts;
}

/** 超过此长度必须抽出题干；抽不出则拒比对（避免整篇材料假阳性） */
const SIMILARITY_LONG_CONTENT = 160;

/** 近完全相同才允许并查集合并多题；软相似只报 pair，禁止 A≈B≈C 串成大簇 */
export const INVENTORY_CLUSTER_MERGE_THRESHOLD = 0.98;

const LISTENING_BOILERPLATE =
  /^(listen to the (following )?(conversation|passage|lecture|talk|discussion)[^.?!？！]*[.?!？！]?\s*)+/i;

/** 阅读/听力题干套话（比对这些词会把无关 TOEFL 题打成「相似」） */
const EXAM_FRAME_PHRASES = [
  'which of the following sentences from the passage',
  'which of the following is not mentioned in the passage as',
  'which of the following is not mentioned as',
  'which of the following best summarizes',
  'which of the following is not mentioned',
  'which of the following',
  'according to the passage',
  'according to the law of demand',
  'what can be concluded from the passage about',
  'what can be inferred about',
  'what can be inferred',
  'what does the passage suggest',
  'what is the main purpose of the passage',
  'what is the main topic of the passage',
  'what is the main reason',
  'what is the primary function',
  'what was the main reason',
  'the word',
  'in the passage is closest in meaning to',
  'is closest in meaning to',
  'expresses an opinion rather than a fact',
  'rather than a fact',
  'the author\'s purpose in mentioning',
  'the author\'s view on',
  'why does the professor mention the example of',
  'why does the author mention',
  'listen to the following conversation',
  'listen to the conversation',
  'of the passage',
  'in the passage',
  'from the passage',
  'the passage',
  '下列哪项',
  '根据短文',
  '根据文章',
  '根据原文',
].sort((a, b) => b.length - a.length);

const LATIN_STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'but',
  'if',
  'of',
  'in',
  'on',
  'at',
  'to',
  'for',
  'from',
  'with',
  'as',
  'by',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'do',
  'does',
  'did',
  'can',
  'could',
  'would',
  'will',
  'should',
  'may',
  'might',
  'must',
  'what',
  'why',
  'how',
  'when',
  'where',
  'which',
  'who',
  'whom',
  'whose',
  'that',
  'this',
  'these',
  'those',
  'it',
  'its',
  'into',
  'about',
  'over',
  'under',
  'not',
  'no',
  'yes',
  'than',
  'then',
  'also',
  'just',
  'only',
  'more',
  'most',
  'such',
  'one',
  'following',
  'mentioned',
  'author',
  'authors',
  'speaker',
  'professor',
  'student',
  'best',
  'main',
  'primary',
  'le',
  'la',
  'les',
  'un',
  'une',
  'des',
  'du',
  'de',
  'et',
  'est',
  'sont',
  'quel',
  'quelle',
  'quels',
  'quelles',
]);

/**
 * 近重复比对用文本：去掉 passage / 抽题干；长文无题干则空串（拒比对）。
 */
export function normalizeSimilarityText(content: unknown, passage?: unknown): string {
  let text = String(content ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';

  const passageText = String(passage ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (passageText.length >= 40) {
    const stripped = stripSubstringIgnoreCase(text, passageText);
    if (stripped.length >= 12) {
      text = stripped;
    }
  }

  text = text.replace(LISTENING_BOILERPLATE, '').trim();
  if (!text) return '';

  if (text.length > SIMILARITY_LONG_CONTENT) {
    const stem = extractInterrogativeStem(text);
    if (stem && stem.length >= 12) {
      return stem.toLowerCase();
    }
    // 整篇材料 / 无清晰题干：不参与相似比对
    return '';
  }

  return text.toLowerCase();
}

function stripSubstringIgnoreCase(haystack: string, needle: string): string {
  const lowerHay = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let start = 0;
  let out = '';
  while (start < haystack.length) {
    const at = lowerHay.indexOf(lowerNeedle, start);
    if (at < 0) {
      out += haystack.slice(start);
      break;
    }
    out += haystack.slice(start, at);
    start = at + needle.length;
  }
  return out.replace(/\s+/g, ' ').trim();
}

function extractInterrogativeStem(text: string): string | null {
  const chunks =
    text.match(/[^.!?？！:]+[.!?？！:]?/g)?.map((c) => c.trim()).filter(Boolean) ?? [];
  const questions = chunks.filter(
    (c) =>
      /[?？]\s*$/.test(c) ||
      /closest in meaning/i.test(c) ||
      /^(what|why|how|when|where|which|who|whom|whose|does|do|did|is|are|was|were|can|could|would|will|should|according to|on which|in which|the word|the author|根据|下列|以下|哪|为什么|怎么|何时|何处)/i.test(
        c
      )
  );
  if (questions.length === 0) return null;
  const substantial = questions.filter((q) => q.length >= 20);
  if (substantial.length === 1) return substantial[0]!;
  if (substantial.length > 1) {
    // 题干通常短于材料句；取最短实质问句
    return substantial.reduce((a, b) => (a.length <= b.length ? a : b));
  }
  return questions.join(' ').trim() || null;
}

/**
 * 检测相似的题目（内容近重复 = 题干+选项文字近拷，≠ 主题 / ≠ passage 复用）。
 * - 指纹经 buildSimilarityFingerprint；拉丁文去套话/停用词实义词 Jaccard；CJK 字符级
 * - 批内校验与 Content Inventory 近重复扫描共用
 */
export function detectSimilarQuestions(
  questions: SimilarQuestionInput[],
  options?: DetectSimilarQuestionsOptions
): SimilarQuestionPair[] {
  const warnings: SimilarQuestionPair[] = [];
  const similarityThreshold = options?.similarityThreshold ?? 0.8;
  const capped =
    options?.maxQuestions != null && options.maxQuestions > 0
      ? questions.slice(0, options.maxQuestions)
      : questions;
  const maxPairs = options?.maxPairs;

  const fingerprints = capped.map((q) => buildSimilarityFingerprint(q));

  for (let i = 0; i < fingerprints.length; i++) {
    for (let j = i + 1; j < fingerprints.length; j++) {
      const a = fingerprints[i]!;
      const b = fingerprints[j]!;
      if (!a || !b) continue;

      const similarity = calculateSimilarity(a, b);

      if (similarity >= similarityThreshold) {
        warnings.push({
          index1: i,
          index2: j,
          similarity: Math.round(similarity * 100) / 100,
        });
        if (maxPairs != null && warnings.length >= maxPairs) {
          return warnings;
        }
      }
    }
  }

  return warnings;
}

const CJK_OR_SYLLABARY = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/;

function stripExamFrames(text: string): string {
  let out = ` ${text.toLowerCase()} `;
  for (const phrase of EXAM_FRAME_PHRASES) {
    out = out.split(phrase).join(' ');
  }
  return out.replace(/\s+/g, ' ').trim();
}

function tokenizeForSimilarity(str: string): Set<string> {
  if (CJK_OR_SYLLABARY.test(str)) {
    return new Set(str.replace(/\s+/g, '').split('').filter(Boolean));
  }
  const framed = stripExamFrames(str);
  const words = framed.match(/[a-z0-9']+/g) ?? [];
  return new Set(
    words.filter((w) => w.length > 2 && !LATIN_STOPWORDS.has(w) && !/^\d+$/.test(w))
  );
}

/**
 * 计算两个字符串的相似度（自适应 Jaccard；拉丁文为实义词）
 */
function calculateSimilarity(str1: string, str2: string): number {
  if (str1 === str2) return 1.0;
  if (str1.length === 0 || str2.length === 0) return 0.0;

  const set1 = tokenizeForSimilarity(str1);
  const set2 = tokenizeForSimilarity(str2);
  // 套话剥光后无实词 → 不判相似（避免空∩空=1）
  if (set1.size === 0 || set2.size === 0) return 0.0;

  let intersection = 0;
  for (const token of set1) {
    if (set2.has(token)) intersection += 1;
  }
  const union = set1.size + set2.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/**
 * 自定义验证规则类型
 */
export interface CustomValidationRule {
  name: string;
  validate: (question: any) => ValidationError | null;
  priority?: number; // 优先级，数字越小优先级越高
}

/**
 * 全局自定义验证规则注册表
 */
const customValidationRules: CustomValidationRule[] = [];

/**
 * 注册自定义验证规则
 */
export function registerValidationRule(rule: CustomValidationRule): void {
  // 移除同名的旧规则
  const index = customValidationRules.findIndex((r) => r.name === rule.name);
  if (index >= 0) {
    customValidationRules.splice(index, 1);
  }

  // 添加新规则
  customValidationRules.push(rule);

  // 按优先级排序
  customValidationRules.sort((a, b) => (a.priority || 100) - (b.priority || 100));
}

/**
 * 移除自定义验证规则
 */
export function unregisterValidationRule(name: string): void {
  const index = customValidationRules.findIndex((r) => r.name === name);
  if (index >= 0) {
    customValidationRules.splice(index, 1);
  }
}

/**
 * 应用自定义验证规则
 */
function applyCustomValidationRules(question: any): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const rule of customValidationRules) {
    try {
      const error = rule.validate(question);
      if (error) {
        errors.push(error);
      }
    } catch (err) {
      console.warn(`[Validator] 自定义验证规则 "${rule.name}" 执行失败:`, err);
    }
  }

  return errors;
}

/**
 * 增强的题目验证函数，支持自定义规则
 */
export function validateQuestionWithCustomRules(
  question: any,
  customRules?: CustomValidationRule[]
): ValidationResult {
  // 临时注册自定义规则
  if (customRules && customRules.length > 0) {
    customRules.forEach((rule) => registerValidationRule(rule));
  }

  try {
    // 执行基础验证
    const baseResult = validateQuestion(question);

    // 应用自定义验证规则
    const customErrors = applyCustomValidationRules(question);

    return {
      valid: baseResult.valid && customErrors.length === 0,
      errors: [...baseResult.errors, ...customErrors],
    };
  } finally {
    // 清理临时注册的规则
    if (customRules && customRules.length > 0) {
      customRules.forEach((rule) => unregisterValidationRule(rule.name));
    }
  }
}

// ==================== 考试特定验证函数 ====================

/**
 * 使用考试特定定义验证题目
 */
export function validateQuestionWithExamDefinition(
  question: any,
  examId: string,
  typeId: string,
  options?: ValidateOptions
): ValidationResult {
  const mode = options?.mode;
  const isPreview = mode === 'preview';

  // 1. 基础验证（不带 examId/typeId，避免递归）
  const baseResult = validateQuestionBase(question, {
    mode,
    allowedQuestionTypes: options?.allowedQuestionTypes,
  });

  // 2. 获取考试特定定义
  const definition = getExamQuestionTypeDefinition(examId, typeId);
  if (!definition) {
    return finalizeValidationResult([
      {
        field: 'type',
        message: `未知的考试特定类型: ${examId}/${typeId}`,
        code: 'UNKNOWN_EXAM_QUESTION_TYPE',
      },
    ]);
  }

  // 3. 验证基础类型匹配
  if (question.type !== definition.baseType) {
    return finalizeValidationResult([
      {
        field: 'type',
        message: `类型不匹配: 期望 ${definition.baseType}, 得到 ${question.type}`,
        code: 'TYPE_MISMATCH',
      },
    ]);
  }

  const isOpenEnded = definition.responseMode === 'open_ended';
  let baseErrors = [...baseResult.errors];

  if (isOpenEnded) {
    baseErrors = baseErrors.filter(
      (error) => error.code !== 'MISSING_ANSWER' && error.code !== 'CONTENT_SEMANTIC_WARNING'
    );

    if (looksLikeOpenEndedClozeStem(question.content)) {
      baseErrors.push({
        field: 'content',
        message: '开放作答须为可评分的写作/口语任务，禁止完形填空式题干',
        code: 'OPEN_ENDED_CLOZE_STEM',
      });
    }

    if (Array.isArray(question.options) && question.options.length > 0) {
      baseErrors.push({
        field: 'options',
        message: '开放作答题目不应包含选择题选项',
        code: 'OPEN_ENDED_HAS_OPTIONS',
      });
    }

    if (!isAbsentOpenEndedAnswer(question.correctAnswer) && typeof question.correctAnswer !== 'string') {
      baseErrors.push({
        field: 'correctAnswer',
        message:
          '开放作答的 correctAnswer 须为字符串提纲/范文要点，或留空；勿使用填空答案数组',
        code: 'OPEN_ENDED_ANSWER_TYPE',
      });
    }
  }

  // 3b. 验证 requiredStimulus
  const stimulusErrors: ValidationError[] = [];
  if (definition.requiredStimulus?.length) {
    const actual = normalizeStimulus(question.stimulus);
    for (const required of definition.requiredStimulus) {
      if (!actual.includes(required)) {
        stimulusErrors.push({
          field: 'stimulus',
          message: `考试题型 ${typeId} 要求材料 ${required}`,
          code: 'MISSING_REQUIRED_STIMULUS',
        });
      }
    }
  }

  // 4. 验证额外字段（preview 跳过套卷编排字段的 required；*Level 可回退到 level）
  const extraErrors: ValidationError[] = [];
  const questionRecord = question as Record<string, unknown>;
  if (definition.extraFields) {
    for (const [fieldName, fieldDef] of Object.entries(definition.extraFields)) {
      const effectiveDef =
        isPreview && PAPER_ORCHESTRATION_FIELD_IDS.has(fieldName)
          ? { ...fieldDef, required: false }
          : fieldDef;
      const error = validateField(
        fieldName,
        resolveExamLevelFieldValue(fieldName, questionRecord),
        effectiveDef
      );
      if (error) extraErrors.push(error);
    }
  }

  // 5. 验证字段覆盖
  if (definition.fieldOverrides) {
    for (const [fieldName, fieldDef] of Object.entries(definition.fieldOverrides)) {
      const effectiveDef =
        isPreview && PAPER_ORCHESTRATION_FIELD_IDS.has(fieldName)
          ? { ...(fieldDef as FieldDefinition), required: false }
          : (fieldDef as FieldDefinition);
      const error = validateField(
        fieldName,
        resolveExamLevelFieldValue(fieldName, questionRecord),
        effectiveDef
      );
      if (error) extraErrors.push(error);
    }
  }

  // 6. 执行自定义验证
  let customErrors: ValidationError[] = [];
  if (definition.customValidator) {
    customErrors = definition.customValidator(question);
  }

  return finalizeValidationResult([...baseErrors, ...stimulusErrors, ...extraErrors, ...customErrors]);
}

/**
 * 验证题目是否符合 Section 约束
 */
export function validateQuestionAgainstSection(
  question: any,
  constraint: SectionQuestionTypeConstraint
): ValidationResult {
  const errors: ValidationError[] = [];

  // 验证字段约束
  if (constraint.fieldConstraints) {
    for (const [field, constraints] of Object.entries(constraint.fieldConstraints)) {
      if (constraints.required && question[field] === undefined) {
        errors.push({
          field,
          message: `该 Section 中 ${field} 是必填字段`,
          code: 'SECTION_REQUIRED_FIELD',
        });
      }

      if (
        constraints.allowedValues !== undefined &&
        !constraints.allowedValues.includes(question[field])
      ) {
        errors.push({
          field,
          message: `${field} 必须是以下值之一: ${constraints.allowedValues.join(', ')}`,
          code: 'SECTION_ALLOWED_VALUES',
        });
      }

      if (constraints.min !== undefined && Number(question[field]) < constraints.min) {
        errors.push({
          field,
          message: `${field} 最小值为 ${constraints.min}`,
          code: 'SECTION_MIN_VALUE',
        });
      }

      if (constraints.max !== undefined && Number(question[field]) > constraints.max) {
        errors.push({
          field,
          message: `${field} 最大值为 ${constraints.max}`,
          code: 'SECTION_MAX_VALUE',
        });
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * 完整的题目验证（包含考试定义和 Section 约束）
 */
export function validateQuestionComplete(
  question: any,
  options: {
    examId?: string;
    typeId?: string;
    sectionConstraint?: SectionQuestionTypeConstraint;
    mode?: ValidateOptions['mode'];
    allowedQuestionTypes?: string[];
  } = {}
): ValidationResult {
  let result: ValidationResult;

  if (options.examId && options.typeId) {
    result = validateQuestionWithExamDefinition(question, options.examId, options.typeId, {
      mode: options.mode,
      allowedQuestionTypes: options.allowedQuestionTypes,
    });
  } else {
    result = validateQuestionBase(question, {
      mode: options.mode,
      allowedQuestionTypes: options.allowedQuestionTypes,
    });
  }

  if (options.sectionConstraint) {
    const sectionResult = validateQuestionAgainstSection(question, options.sectionConstraint);
    result = finalizeValidationResult([...result.errors, ...sectionResult.errors]);
  }

  return result;
}

/**
 * 验证题目类型是否被 Section 支持
 */
export function isQuestionTypeAllowedInSection(
  questionTypeId: string,
  sectionConstraints: SectionQuestionTypeConstraint[]
): boolean {
  return sectionConstraints.some((c) => c.questionTypeId === questionTypeId);
}

/**
 * 获取 Section 中某类型的数量限制
 */
export function getSectionCountLimit(
  questionTypeId: string,
  sectionConstraints: SectionQuestionTypeConstraint[]
): { min: number; max: number } | undefined {
  const constraint = sectionConstraints.find((c) => c.questionTypeId === questionTypeId);
  return constraint?.countLimit;
}

/**
 * 获取题目在考试中应该使用的类型ID
 * 如果考试有特定定义，返回考试特定的类型ID；否则返回基础类型
 */
export function getEffectiveQuestionTypeId(baseType: string, examId?: string): string {
  if (!examId) return baseType;

  const examSpecific = getQuestionTypeForExam(examId, baseType as any);
  return examSpecific?.id ?? baseType;
}
